import { Menu, Transition } from '@headlessui/react'
import {
    ArrowRightOnRectangleIcon,
    ChevronDownIcon,
    Cog6ToothIcon,
    EyeIcon,
    HomeIcon,
    KeyIcon,
    UserGroupIcon,
    UsersIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { cn } from '@/utils/cn'

import logo from '../../../logo-black-no-bg.svg'

import { buildSession, OrganizationRole, PreviewSessionProvider } from './previewSession'

// Routes are mounted under this base; the ported screens link with it prefixed.
export const BASE = '/admin/provider-preview'

const PROVIDER_NAV = [
    { text: 'Dashboard', href: `${BASE}/dashboard`, icon: HomeIcon },
    { text: 'Team Members', href: `${BASE}/team`, icon: UserGroupIcon, adminOnly: true },
    { text: 'Clients', href: `${BASE}/clients`, icon: UsersIcon },
    { text: 'Licenses', href: `${BASE}/licenses`, icon: KeyIcon },
    { text: 'Settings', href: `${BASE}/settings`, icon: Cog6ToothIcon },
]

const SCREENS = [
    { text: 'Login', href: `${BASE}/login` },
    { text: 'Dashboard', href: `${BASE}/dashboard` },
    { text: 'Team Members', href: `${BASE}/team` },
    { text: 'Team member detail', href: `${BASE}/team/mem_coord1` },
    { text: 'Clients', href: `${BASE}/clients` },
    { text: 'Client detail', href: `${BASE}/clients/lic_001` },
    { text: 'Client detail (at risk)', href: `${BASE}/clients/lic_009` },
    { text: 'Former client', href: `${BASE}/clients/former/fpat_001` },
    { text: 'Licenses', href: `${BASE}/licenses` },
    { text: 'Settings', href: `${BASE}/settings` },
]

const ROLE_LABELS: Record<string, string> = {
    admin: 'Administrator',
    coordinator: 'Coordinator',
    viewer: 'Viewer',
}

function initials(name?: string | null, email?: string | null): string {
    const source = (name || email || '?').trim()
    const parts = source.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return source.slice(0, 2).toUpperCase()
}

export default function ProviderPreviewLayout() {
    const location = useLocation()
    const navigate = useNavigate()
    // Role lives here so it persists across child routes (the provider wraps the
    // Outlet) and the switcher is part of the demo chrome.
    const [role, setRole] = useState<OrganizationRole>('admin')
    const session = buildSession(role)

    // Login renders full-bleed, like the live portal's standalone login.
    const isStandalone = location.pathname.endsWith('/login') || location.pathname === BASE || location.pathname === `${BASE}/`

    function handleSignOut() {
        navigate(`${BASE}/login`)
    }

    return (
        <PreviewSessionProvider value={{ role, setRole, session }}>
            <div className='min-h-screen bg-neutral-50'>
                <PreviewBanner role={role} setRole={setRole} />

                {isStandalone ? (
                    <div className='flex flex-col min-h-screen'>
                        <Outlet />
                    </div>
                ) : (
                    <div className='flex'>
                        {/* Sidebar — mirrors the live provider shell */}
                        <aside className='hidden lg:flex flex-col w-64 min-h-screen bg-brand-600 sticky top-10'>
                            <div className='px-6 py-6 border-b border-white/10'>
                                <span className='block text-xs uppercase tracking-wider text-white/60 whitespace-nowrap mb-1'>
                                    BrightPath Provider
                                </span>
                                <span className='block text-sm font-semibold text-white break-words leading-snug'>
                                    {session.organization.displayName}
                                </span>
                            </div>
                            <nav className='flex-1 px-4 py-6'>
                                <ul className='space-y-1'>
                                    {PROVIDER_NAV.filter((link) => !link.adminOnly || role === 'admin').map((link) => (
                                        <li key={link.href}>
                                            <NavLink
                                                to={link.href}
                                                end
                                                className={({ isActive }) =>
                                                    cn(
                                                        'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                                                        isActive
                                                            ? 'bg-white/15 text-white'
                                                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                                                    )
                                                }
                                            >
                                                <link.icon className='w-5 h-5 mr-3' />
                                                {link.text}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </aside>

                        <div className='flex-1 min-h-screen flex flex-col'>
                            {/* Top bar with the user menu, mirroring the family app */}
                            <header className='bg-white border-b border-neutral-200 px-4 sm:px-6'>
                                <div className='flex items-center justify-between h-14'>
                                    <img src={logo} alt='BrightPath' className='h-11 w-auto' />
                                    <Menu as='div' className='relative'>
                                        <Menu.Button className='flex items-center gap-2 p-1.5 rounded-lg hover:bg-neutral-50'>
                                            <span className='sr-only'>Open user menu</span>
                                            <span className='w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-semibold flex items-center justify-center'>
                                                {initials(session.user.name, session.user.email)}
                                            </span>
                                            <span className='hidden sm:flex flex-col items-start leading-tight'>
                                                <span className='text-sm font-medium text-neutral-900'>
                                                    {session.user.name || session.user.email}
                                                </span>
                                                <span className='text-xs text-neutral-500'>
                                                    {ROLE_LABELS[role] ?? ''}
                                                </span>
                                            </span>
                                            <ChevronDownIcon className='w-4 h-4 text-neutral-400' aria-hidden='true' />
                                        </Menu.Button>
                                        <Transition
                                            enter='transition ease-out duration-100'
                                            enterFrom='transform opacity-0 scale-95'
                                            enterTo='transform opacity-100 scale-100'
                                            leave='transition ease-in duration-75'
                                            leaveFrom='transform opacity-100 scale-100'
                                            leaveTo='transform opacity-0 scale-95'
                                        >
                                            <Menu.Items className='absolute right-0 z-20 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-gray-900/5 focus:outline-none py-1'>
                                                <div className='px-3 py-2 border-b border-neutral-100'>
                                                    <div className='text-sm font-medium text-neutral-900 truncate'>
                                                        {session.user.name || session.user.email}
                                                    </div>
                                                    <div className='text-xs text-neutral-500 truncate'>{session.user.email}</div>
                                                    <div className='text-xs text-neutral-500'>{ROLE_LABELS[role] ?? ''}</div>
                                                </div>
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            onClick={handleSignOut}
                                                            className={cn(
                                                                'w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 text-left',
                                                                active ? 'bg-neutral-50' : ''
                                                            )}
                                                        >
                                                            <ArrowRightOnRectangleIcon className='w-4 h-4' />
                                                            Sign out
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>
                            </header>

                            <main className='flex-1'>
                                <Outlet />
                            </main>
                        </div>
                    </div>
                )}
            </div>
        </PreviewSessionProvider>
    )
}

function PreviewBanner({ role, setRole }: { role: OrganizationRole; setRole: (role: OrganizationRole) => void }) {
    return (
        <div className='sticky top-0 z-50 bg-amber-100 border-b border-amber-300 px-4 py-2'>
            <div className='flex flex-wrap items-center justify-between gap-2 max-w-7xl mx-auto'>
                <div className='flex items-center gap-2 text-xs sm:text-sm text-amber-900'>
                    <EyeIcon className='w-4 h-4 flex-shrink-0' />
                    <span className='font-semibold'>PREVIEW MODE</span>
                    <span className='hidden sm:inline'>Provider portal demo</span>
                </div>
                <div className='flex items-center gap-2'>
                    <RoleSwitcher role={role} setRole={setRole} />
                    <ScreenJump />
                    <Link to='/admin' className='text-xs text-amber-900 underline hover:text-amber-700'>
                        Exit to admin
                    </Link>
                </div>
            </div>
        </div>
    )
}

function RoleSwitcher({ role, setRole }: { role: OrganizationRole; setRole: (role: OrganizationRole) => void }) {
    return (
        <label className='flex items-center gap-1.5 text-xs text-amber-900'>
            <span className='hidden sm:inline'>View as</span>
            <select
                value={role}
                onChange={(e) => setRole(e.target.value as OrganizationRole)}
                className='text-xs bg-white border border-amber-300 rounded px-2 py-1 text-amber-900'
            >
                <option value='admin'>Admin</option>
                <option value='coordinator'>Coordinator</option>
                <option value='viewer'>Viewer</option>
            </select>
        </label>
    )
}

function ScreenJump() {
    const location = useLocation()
    const navigate = useNavigate()
    return (
        <select
            value={SCREENS.some((s) => s.href === location.pathname) ? location.pathname : ''}
            onChange={(e) => {
                // react-router navigation keeps the role state intact (a full
                // reload would reset it back to admin).
                if (e.target.value) navigate(e.target.value)
            }}
            className='text-xs bg-white border border-amber-300 rounded px-2 py-1 text-amber-900'
        >
            <option value='' disabled>
                Jump to screen...
            </option>
            {SCREENS.map((s) => (
                <option key={s.href} value={s.href}>
                    {s.text}
                </option>
            ))}
        </select>
    )
}
